<script>
  import { onDestroy } from "svelte";
  import Icon from '#lib/Icon.svelte';
  import Popover from '#lib/Popover.svelte';
  import { nanoid } from '#lib/util.js';

  let {
    /** @type {string|SvelteSnippet} The text of the trigger */
    title,
    /** @type {string|SvelteSnippet} Custom trigger */
    children,
    /** @type {string|SvelteSnippet} The text of the tooltip */
    description,
    /** @type {string} The icon to show in the tooltip */
    icon = 'tooltip-circle-outline',
    /** @type {'top'|'top-start'|'top-end'|'top-flush'|'bottom'|'bottom-start'|'bottom-end'|'bottom-flush'|'left'|'left-start'|'left-end'|'right'|'right-start'|'right-end'|'center'} Defines the tooltip's position relative to its anchor */
    placement = 'bottom-start',
    text_style = 'text-control',
    /** @type {'normal'|'textual'|'ghostly'} Defines the tooltip's appearance */
    variant = 'normal',
    class: css_class,
    show_icon = true,
    /** @type {number} Delay in ms before showing tooltip on hover */
    hover_delay = 400,
    /** @type {HTMLButtonElement} Reference to the trigger button element */
    trigger_ref = $bindable(),
    /** @type {Popover} Reference to the popover element */
    popover_ref = $bindable(),
    /** @type {boolean} Read only state of the tooltip's open state */
    is_open = $bindable(false),
  } = $props();

  // Generate unique ID for the popover
  const id = `tooltip_${nanoid()}`;
  let hover_timeout;

  // Handle hover events
  function handle_mouseenter() {
    if (hover_timeout) clearTimeout(hover_timeout);
    hover_timeout = setTimeout(() => {
      if (popover_ref && !is_open) {
        // Small delay to ensure popover is mounted
        setTimeout(() => popover_ref?.showPopover(), 0);
      }
    }, hover_delay);
  }

  function handle_mouseleave() {
    if (hover_timeout) clearTimeout(hover_timeout);
    popover_ref?.hidePopover();
  }

  // Handle click events
  function handle_click(e) {
    e.preventDefault();
    if (popover_ref && !is_open) {
      // Small delay to ensure popover is mounted
      setTimeout(() => popover_ref?.showPopover(), 0);
    }
  }

  // Cleanup
  onDestroy(() => {
    if (hover_timeout) clearTimeout(hover_timeout);
  });
</script>

<button 
  bind:this={trigger_ref}
  {id}
  class="tooltip-trigger {css_class} {variant}"
  onclick={handle_click}
  onmouseenter={handle_mouseenter}
  onmouseleave={handle_mouseleave}
  aria-label={typeof description === 'string' ? description : ''}
>
  {#if title || children || description}
    {#if typeof title === 'string' && title}
      <span class='label {text_style}'>{title}</span>
    {:else if typeof title === 'function'}
      {@render title()}
    {:else if typeof children === 'function'}
      {@render children()}
    {/if}
    {#if show_icon && typeof title !== 'function'}
      <Icon name={icon} style='--icon-color1: var(--color-default-foreground-500)' />
    {/if}
  {/if}
</button>

<Popover
  {id}
  anchor={trigger_ref}
  bind:ref={popover_ref}
  bind:is_open
  {placement}
  class='tooltip-popover bg'
  mode='manual'
  offset={-10}
>
  {#if description && typeof description === 'string'}   
    <p class="tooltip-content px py p-3 text-control text-balance">
      {description}
    </p>
  {:else if description && typeof description === 'function'}
    <div class="tooltip-content px py p-3">
      {@render description()} 
    </div>
  {/if}
</Popover>

<style lang="postcss">
  .tooltip-trigger {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--size-2);
    cursor: default;
    border: none;
    background: none;
    padding: 0;
    margin: 0;

    &.ghostly,
    &.normal {
      min-height: min(var(--input-min-height), 100%);
      min-width: var(--input-min-height);
      border-radius: var(--size-6);

      &:has(.label) {
        background-color: var(--color-default-background-50);
        padding-inline-start: var(--size-3);
        padding-inline-end: var(--size-2);
      }

      &:not(:has(.button-label)) {
        padding-inline: var(--size-2);
        padding-block: var(--input-block-spacing);
      }
    }

    &.textual {
      text-decoration: underline;
      text-decoration-style: dotted;
      text-decoration-color: var(--color-default-foreground);
      text-decoration-thickness: 0.5px;
      text-underline-offset: 2px;
    }
  }

  :global(.tooltip-popover) {
    max-width: min(80vw, 30ch);
  }
  .tooltip-content, .tooltip-content * {
    text-transform: none;
    letter-spacing: normal;
    font-size: max(var(--control-font-size), 12px);
    color: var(--color-background, var(--color-default-background));
    background-color: var(--color-foreground, var(--color-default-foreground));
  }
</style>